import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Switch,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Tabs,
  Tab,
  alpha,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaArrowLeft,
  FaCheck,
  FaEdit,
  FaFileCsv,
  FaFilePdf,
  FaGraduationCap,
  FaPlus,
  FaTimes,
  FaTrash,
  FaUsers,
  FaBookOpen,
  FaFilter,
} from "react-icons/fa";
import InstStudentPage from "./instStudentPage";
import { API_URL } from "../config";
import { toast } from "react-toastify";
import DeleteDialog from "../components/dialog/delete";
import LoadingPage from "../components/loading";
import SubjectModal from "../components/modal/subjectModal";

// ─── Design Tokens ──────────────────────────────────────────────────────────
const tokens = {
  sapphire: "#1D4ED8",
  sapphireLight: "#3B82F6",
  sapphirePale: "#EFF6FF",
  sapphireMid: "#DBEAFE",
  emerald: "#059669",
  emeraldPale: "#ECFDF5",
  rose: "#DC2626",
  rosePale: "#FEF2F2",
  amber: "#D97706",
  slate900: "#0F172A",
  slate700: "#334155",
  slate500: "#64748B",
  slate300: "#CBD5E1",
  slate100: "#F1F5F9",
  slate50: "#F8FAFC",
  white: "#FFFFFF",
  shadow: "0 1px 3px rgba(15,23,42,0.06), 0 4px 16px rgba(15,23,42,0.04)",
  shadowMd: "0 4px 24px rgba(15,23,42,0.08), 0 1px 4px rgba(15,23,42,0.04)",
  shadowLg: "0 8px 40px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.06)",
  radius: "14px",
  radiusSm: "10px",
  radiusXs: "7px",
};

// ─── Motion Variants ────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.42, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 },
  }),
  exit: { opacity: 0, y: -10, transition: { duration: 0.22 } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.36, ease: [0.22, 1, 0.36, 1] },
  },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.18 } },
};

const slideTab = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] },
  },
  exit: { opacity: 0, x: -20, transition: { duration: 0.2 } },
};

// ─── Tab Panel Helper ────────────────────────────────────────────────────────
function TabPanel({ children, value, index }) {
  return (
    <AnimatePresence mode="wait">
      {value === index && (
        <motion.div
          key={index}
          variants={slideTab}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <Box sx={{ pt: 3 }}>{children}</Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Stat Pill ───────────────────────────────────────────────────────────────
function StatPill({
  label,
  value,
  color = tokens.sapphire,
  bg = tokens.sapphirePale,
}) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.4,
        borderRadius: 99,
        background: bg,
        border: `1px solid ${alpha(color, 0.18)}`,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          color,
          letterSpacing: "0.3px",
        }}
      >
        {value} {label}
      </Typography>
    </Box>
  );
}

// ─── Subject Card ─────────────────────────────────────────────────────────────

function SubjectCard({
  s,
  index,
  handleSubjectClick,
  handleEditSubject,
  onConfirmDelete,
  toggleSubjectActive,
  toggleSubjectApprove,
  handleDownloadPDF,
  handleDownloadCSV,
  isLoading, // this card is the one being fetched
  disabled, // another card is being fetched — lock all actions
}) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      exit="exit"
      custom={index}
      layout
      whileHover={!disabled ? { y: -2, transition: { duration: 0.2 } } : {}}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: tokens.radius,
          background: tokens.white,
          border: `1px solid ${
            isLoading ? alpha(tokens.sapphire, 0.45) : tokens.slate300
          }`,
          boxShadow: isLoading ? tokens.shadowMd : tokens.shadow,
          transition: "border-color 0.25s, box-shadow 0.25s, opacity 0.2s",
          opacity: disabled && !isLoading ? 0.55 : 1,
          "&:hover": !disabled
            ? {
                borderColor: alpha(tokens.sapphire, 0.4),
                boxShadow: tokens.shadowMd,
              }
            : {},
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          {/* ── Left ── */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <motion.div
              whileHover={!disabled ? { scale: 1.08, rotate: 3 } : {}}
              transition={{ duration: 0.2 }}
            >
              <Box
                onClick={() => !disabled && handleSubjectClick(s.id, s.name)}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: tokens.radiusSm,
                  background: isLoading
                    ? `linear-gradient(135deg, ${tokens.sapphireMid}, ${tokens.sapphire})`
                    : `linear-gradient(135deg, ${tokens.sapphirePale}, ${tokens.sapphireMid})`,
                  border: `1.5px solid ${alpha(
                    tokens.sapphire,
                    isLoading ? 0.5 : 0.2
                  )}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: disabled ? "not-allowed" : "pointer",
                  flexShrink: 0,
                  transition: "background 0.25s",
                }}
              >
                {/* Spinner replaces icon while loading */}
                {isLoading ? (
                  <CircularProgress
                    size={18}
                    thickness={4.5}
                    sx={{ color: "#fff" }}
                  />
                ) : (
                  <FaGraduationCap size={18} color={tokens.sapphire} />
                )}
              </Box>
            </motion.div>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography
                  onClick={() => !disabled && handleSubjectClick(s.id, s.name)}
                  sx={{
                    fontWeight: 800,
                    color: isLoading ? tokens.sapphire : tokens.slate900,
                    fontSize: { xs: "0.88rem", sm: "0.95rem" },
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                    cursor: disabled ? "not-allowed" : "pointer",
                    transition: "color 0.2s",
                    "&:hover": !disabled ? { color: tokens.sapphire } : {},
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {s.name}
                </Typography>

                {/* Inline "Checking…" label — only on the active card */}
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: tokens.sapphire,
                        letterSpacing: "0.04em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Checking…
                    </Typography>
                  </motion.div>
                )}
              </Stack>

              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                mt={0.5}
                flexWrap="wrap"
                gap={0.5}
              >
                <StatPill
                  label={s.is_active ? "Active" : "Inactive"}
                  value=""
                  color={s.is_active ? tokens.emerald : tokens.slate500}
                  bg={s.is_active ? tokens.emeraldPale : tokens.slate100}
                />
                <StatPill
                  label={s.is_aprove ? "Done" : "Pending"}
                  value=""
                  color={s.is_aprove ? tokens.sapphire : tokens.amber}
                  bg={s.is_aprove ? tokens.sapphirePale : "#FFFBEB"}
                />
              </Stack>
            </Box>
          </Stack>

          {/* ── Right: actions ── */}
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
            gap={0.75}
            sx={{
              flexShrink: 0,
              opacity: disabled ? 0.4 : 1,
              transition: "opacity 0.2s",
            }}
          >
            {/* Active toggle */}
            <Tooltip title={s.is_active ? "Deactivate" : "Activate"} arrow>
              <Switch
                size="small"
                checked={!!s.is_active}
                disabled={disabled}
                onChange={() => toggleSubjectActive(s.id, s.is_active)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: tokens.emerald,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: tokens.emerald,
                  },
                }}
              />
            </Tooltip>

            {/* Edit */}
            <Tooltip title="Edit" arrow>
              <motion.div whileTap={!disabled ? { scale: 0.88 } : {}}>
                <IconButton
                  onClick={() => !disabled && handleEditSubject(s)}
                  disabled={disabled}
                  size="small"
                  sx={{
                    color: tokens.sapphire,
                    background: tokens.sapphirePale,
                    border: `1px solid ${alpha(tokens.sapphire, 0.22)}`,
                    borderRadius: tokens.radiusXs,
                    "&:hover": { background: tokens.sapphireMid },
                  }}
                >
                  <FaEdit size={13} />
                </IconButton>
              </motion.div>
            </Tooltip>

            {/* Delete */}
            <Tooltip title="Delete" arrow>
              <motion.div whileTap={!disabled ? { scale: 0.88 } : {}}>
                <IconButton
                  onClick={() => !disabled && onConfirmDelete(s.id)}
                  disabled={disabled}
                  size="small"
                  sx={{
                    color: tokens.rose,
                    background: tokens.rosePale,
                    border: `1px solid ${alpha(tokens.rose, 0.22)}`,
                    borderRadius: tokens.radiusXs,
                    "&:hover": { background: alpha(tokens.rose, 0.12) },
                  }}
                >
                  <FaTrash size={13} />
                </IconButton>
              </motion.div>
            </Tooltip>

            {/* Approve toggle */}
            <motion.div whileTap={!disabled ? { scale: 0.92 } : {}}>
              <Button
                onClick={() =>
                  !disabled && toggleSubjectApprove(s.id, s.is_aprove)
                }
                disabled={disabled}
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: tokens.radiusXs,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  px: 1.5,
                  minWidth: 0,
                  color: s.is_aprove ? tokens.emerald : tokens.slate500,
                  borderColor: s.is_aprove
                    ? alpha(tokens.emerald, 0.4)
                    : tokens.slate300,
                  "&:hover": {
                    borderColor: s.is_aprove ? tokens.emerald : tokens.sapphire,
                    background: s.is_aprove
                      ? tokens.emeraldPale
                      : tokens.sapphirePale,
                  },
                }}
              >
                {s.is_aprove ? "✓ Done" : "Pending"}
              </Button>
            </motion.div>

            {/* PDF */}
            <Tooltip title="Download PDF" arrow>
              <motion.div whileTap={!disabled ? { scale: 0.92 } : {}}>
                <Button
                  onClick={() => !disabled && handleDownloadPDF(s.id)}
                  disabled={disabled}
                  size="small"
                  startIcon={<FaFilePdf size={11} />}
                  sx={{
                    borderRadius: tokens.radiusXs,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    px: 1.5,
                    background: tokens.rosePale,
                    color: tokens.rose,
                    border: `1px solid ${alpha(tokens.rose, 0.22)}`,
                    boxShadow: "none",
                    "&:hover": {
                      background: alpha(tokens.rose, 0.12),
                      boxShadow: "none",
                    },
                  }}
                >
                  PDF
                </Button>
              </motion.div>
            </Tooltip>

            {/* CSV */}
            <Tooltip title="Download CSV" arrow>
              <motion.div whileTap={!disabled ? { scale: 0.92 } : {}}>
                <Button
                  onClick={() => !disabled && handleDownloadCSV(s.id)}
                  disabled={disabled}
                  size="small"
                  startIcon={<FaFileCsv size={11} />}
                  sx={{
                    borderRadius: tokens.radiusXs,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    px: 1.5,
                    background: tokens.emeraldPale,
                    color: tokens.emerald,
                    border: `1px solid ${alpha(tokens.emerald, 0.22)}`,
                    boxShadow: "none",
                    "&:hover": {
                      background: alpha(tokens.emerald, 0.12),
                      boxShadow: "none",
                    },
                  }}
                >
                  CSV
                </Button>
              </motion.div>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>
    </motion.div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
const InstSubjectPage = () => {
  const { classId } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);
  const [subjectsLoading, setSubjectsLoading] = useState(true);

  const token = useMemo(() => {
    const tokenString = localStorage.getItem("authToken");
    return tokenString ? JSON.parse(tokenString) : null;
  }, []);

  const [subjects, setSubjects] = useState([]);
  const [subjectName, setSubjectName] = useState("");
  const [editingSubject, setEditingSubject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSubjectId, setLoadingSubjectId] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [currentClass, setCurrentClass] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [totalStudents, setTotalStudents] = useState(0);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [studentSubject, setStudentSubject] = useState([]);
  const [openActivityModal, setOpenActivityModal] = useState(false);
  const [selectedActivitySubject, setSelectedActivitySubject] = useState(null);
  const location = useLocation();

  const classData = location.state?.classData;

  const handleTabChange = (_, newValue) => setActiveTab(newValue);

  const getTeacherCode = () => localStorage.getItem("teacher_code");
  const withTeacher = (data = {}) => ({
    teacher_code: getTeacherCode(),
    ...data,
  });

  const fetchData = useCallback(async () => {
    setFetching(true);
    setSubjectsLoading(true);
    try {
      const [subjRes, studRes, classRes] = await Promise.all([
        fetch(`${API_URL}/instclass/${classId}/subjects/`, {
          headers: { Authorization: `Bearer ${token.access}` },
        }),
        fetch(`${API_URL}/instclass/${classId}/students/`, {
          headers: { Authorization: `Bearer ${token.access}` },
        }),
        fetch(`${API_URL}/instclassaproval/${classId}/`, {
          headers: { Authorization: `Bearer ${token.access}` },
        }),
      ]);
      const subjData = await subjRes.json();
      const studData = await studRes.json();
      const classData = await classRes.json();

      setSubjects(
        (subjData || []).slice().sort((a, b) => a.name.localeCompare(b.name))
      );
      setStudents(
        (studData.students || [])
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setTotalStudents(studData.total_students);
      setStudentSubject(
        (studData.subjects_summary ?? [])
          .slice()
          .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
      );
      setCurrentClass(classData || null);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setFetching(false);
      setSubjectsLoading(false);
    }
  }, [classId, token]);

  useEffect(() => {
    if (!token) return navigate("/login");
    fetchData();
  }, [fetchData, navigate, token]);

  const getErrorMessage = (err) => {
    if (!err) return "Something went wrong";
    return err?.message || err?.error || err?.detail || JSON.stringify(err);
  };

  const handleEditSubject = (subj) => {
    setEditingSubject(subj);
    setSubjectName(subj.name);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearSubjectForm = () => {
    setEditingSubject(null);
    setSubjectName("");
  };

  const handleSubjectSubmit = async (e) => {
    e.preventDefault();
    if (!subjectName.trim()) return;
    setLoading(true);
    try {
      const method = editingSubject ? "PUT" : "POST";
      const url = editingSubject
        ? `${API_URL}/instclass/${classId}/subjects/${editingSubject.id}/`
        : `${API_URL}/instclass/${classId}/subjects/`;
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.access}`,
        },
        body: JSON.stringify(withTeacher({ name: subjectName })),
      });
      const data = await res.json().catch(() => null);
      if (res.ok) {
        const updated = editingSubject
          ? subjects.map((s) => (s.id === editingSubject.id ? data : s))
          : [...subjects, data];
        setSubjects(
          updated.slice().sort((a, b) => a.name.localeCompare(b.name))
        );
        clearSubjectForm();
        toast.success("Subject updated successfully!");
      } else {
        toast.error(getErrorMessage(data));
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleSubjectActive = async (subjectId, currentStatus) => {
    const updatedStatus = !currentStatus;
    try {
      const res = await fetch(
        `${API_URL}/instclass/${classId}/subjects/${subjectId}/`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token.access}`,
          },
          body: JSON.stringify(withTeacher({ is_active: updatedStatus })),
        }
      );
      const data = await res.json().catch(() => null);
      if (res.ok) {
        setSubjects((prev) =>
          prev
            .map((s) =>
              s.id === subjectId ? { ...s, is_active: updatedStatus } : s
            )
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      } else {
        toast.error(getErrorMessage(data));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const toggleLClassApprove = async (cId, currentStatus) => {
    const updatedStatus = !currentStatus;
    try {
      const res = await fetch(`${API_URL}/instclassaproval/${cId}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.access}`,
        },
        body: JSON.stringify(withTeacher({ is_approved: updatedStatus })),
      });
      const data = await res.json().catch(() => null);
      if (res.ok) {
        setCurrentClass((prev) =>
          prev ? { ...prev, is_approved: updatedStatus } : null
        );
        setClasses((prev) =>
          prev.map((c) =>
            c.id === cId ? { ...c, is_approved: updatedStatus } : c
          )
        );
      } else {
        toast.error("Error updating approval: " + getErrorMessage(data));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const toggleSubjectApprove = async (subjectId, currentStatus) => {
    const updatedStatus = !currentStatus;
    try {
      const res = await fetch(
        `${API_URL}/instclass/${classId}/subjects/${subjectId}/`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token.access}`,
          },
          body: JSON.stringify(withTeacher({ is_approved: updatedStatus })),
        }
      );
      const data = await res.json().catch(() => null);
      if (res.ok) {
        setSubjects((prev) =>
          prev
            .map((s) =>
              s.id === subjectId ? { ...s, is_approved: updatedStatus } : s
            )
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      } else {
        toast.error("Error updating approval: " + getErrorMessage(data));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteSubject = async () => {
    if (!selectedSubjectId) return;
    setLoadingDelete(true);
    try {
      const res = await fetch(
        `${API_URL}/instclass/${classId}/subjects/${selectedSubjectId}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token.access}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(withTeacher()),
        }
      );
      if (res.ok) {
        setSubjects((prev) => prev.filter((s) => s.id !== selectedSubjectId));
        toast.success("Subject deleted successfully ✅");
      } else {
        const err = await res.json().catch(() => null);
        toast.error("Failed to delete subject: " + getErrorMessage(err));
      }
    } catch (err) {
      toast.error("Something went wrong while deleting ❌");
    } finally {
      setLoadingDelete(false);
      closeConfirmDialog();
    }
  };

  const onConfirmDelete = (subjectId) => {
    setSelectedSubjectId(subjectId);
    setOpenConfirm(true);
  };
  const closeConfirmDialog = () => {
    setOpenConfirm(false);
    setSelectedSubjectId(null);
  };

  const handleSubjectClick = async (subId, subName) => {
    if (loadingSubjectId) return; // prevent double-clicks while loading

    try {
      setLoadingSubjectId(subId);

      const tokenString = localStorage.getItem("authToken");
      const token = JSON.parse(tokenString);
      const teacherCode = localStorage.getItem("teacher_code");

      const res = await fetch(
        `${API_URL}/subject-permission/${classId}/${subId}/?teacher_code=${teacherCode}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token.access}`,
          },
        }
      );

      const data = await res.json();

      if (data.permitted) {
        setSelectedActivitySubject({
          id: subId,
          name: subName,
        });

        setOpenActivityModal(true);
      } else {
        toast.error("You are not allowed ❌");
      }
      
    } catch (err) {
      toast.error("Permission check failed");
    } finally {
      setLoadingSubjectId(null);
    }
  };

  useEffect(() => {
    console.log("openActivityModal:", openActivityModal);
  }, [openActivityModal]);

  const handleExamClick = () => {
    navigate(`/instquestion/${selectedActivitySubject.id}`, {
      state: {
        subjectName: selectedActivitySubject.name,
        classId,
      },
    });

    setOpenActivityModal(false);
  };

  const handleAssignmentClick = () => {
    navigate(`/instassignment/${selectedActivitySubject.id}`, {
      state: {
        subjectName: selectedActivitySubject.name,
        classId,
      },
    });

    setOpenActivityModal(false);
  };

  const downloadFile = async (url, filename) => {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token.access}`, Accept: "*/*" },
    });
    if (!response.ok) throw new Error("Failed to download file");
    const blob = await response.blob();
    if (blob.type === "application/json")
      throw new Error("Failed to download file");
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleDownloadCSV = async (subjectId) => {
    try {
      await downloadFile(
        `${API_URL}/subjects/${subjectId}/exam-scores/csv/`,
        "exam_scores.csv"
      );
    } catch {
      toast.error("Failed to download CSV");
    }
  };

  const handleDownloadPDF = async (subjectId) => {
    try {
      await downloadFile(
        `${API_URL}/subjects/${subjectId}/exam-scores/pdf/`,
        "exam_scores.pdf"
      );
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const handleDownloadStudentCSV = async (classId) => {
    try {
      await downloadFile(
        `${API_URL}/inst-stud/${classId}/download/csv/`,
        "students_data.csv"
      );
    } catch {
      toast.error("Failed to download CSV");
    }
  };

  const handleDownloadStudentPDF = async (classId) => {
    try {
      await downloadFile(
        `${API_URL}/inst-stud/${classId}/download/pdf/`,
        "students_data.pdf"
      );
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  if (fetching) return <LoadingPage />;

  const activeCount = subjects.filter((s) => s.is_active).length;
  console.log(classes);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(160deg, #F0F7FF 0%, #F8FAFC 40%, #EFF6FF 100%)`,
        py: { xs: 3, md: 5 },
        px: { xs: 2, md: 4, lg: 6 },
        fontFamily: "'DM Sans', 'Manrope', sans-serif",
      }}
    >
      {/* ── Page Header ─────────────────────────────────────────────────────── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        custom={0}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3 },
            mb: 3,
            borderRadius: tokens.radius,
            background: tokens.white,
            border: `1px solid ${tokens.slate300}`,
            boxShadow: tokens.shadowMd,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative accent stripe */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: `linear-gradient(90deg, ${tokens.sapphire}, ${tokens.sapphireLight}, #60A5FA)`,
              borderRadius: "14px 14px 0 0",
            }}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
            flexWrap="wrap"
            gap={1.5}
            pt={0.5}
          >
            <motion.div whileHover={{ x: -3 }} whileTap={{ scale: 0.9 }}>
              <IconButton
                onClick={() => navigate(-1)}
                size="medium"
                sx={{
                  color: tokens.sapphire,
                  background: tokens.sapphirePale,
                  border: `1.5px solid ${alpha(tokens.sapphire, 0.22)}`,
                  borderRadius: tokens.radiusSm,
                  "&:hover": { background: tokens.sapphireMid },
                }}
              >
                <FaArrowLeft size={15} />
              </IconButton>
            </motion.div>

            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
                <FaBookOpen size={16} color={tokens.sapphire} />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: tokens.slate900,
                    letterSpacing: "-0.5px",
                    fontSize: { xs: "1.1rem", md: "1.5rem" },
                    lineHeight: 1.2,
                  }}
                >
                  Subjects &amp; Students for {classData.name}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.75}>
                <StatPill label="subjects" value={subjects.length} />
                <StatPill
                  label="active"
                  value={activeCount}
                  color={tokens.emerald}
                  bg={tokens.emeraldPale}
                />
                <StatPill
                  label="students"
                  value={totalStudents}
                  color={tokens.slate700}
                  bg={tokens.slate100}
                />
              </Stack>
            </Box>

            {currentClass && (
              <motion.div whileTap={{ scale: 0.93 }}>
                <Button
                  onClick={() =>
                    toggleLClassApprove(
                      currentClass.id,
                      currentClass.is_approved
                    )
                  }
                  variant="contained"
                  size="small"
                  startIcon={
                    currentClass?.is_approved ? (
                      <FaCheck size={11} />
                    ) : (
                      <FaTimes size={11} />
                    )
                  }
                  sx={{
                    borderRadius: 99,
                    px: 2.5,
                    py: 0.9,
                    fontWeight: 800,
                    fontSize: "0.78rem",
                    textTransform: "none",
                    letterSpacing: "0.3px",
                    background: currentClass?.is_approved
                      ? `linear-gradient(135deg, ${tokens.emerald}, #047857)`
                      : tokens.slate100,
                    border: currentClass?.is_approved
                      ? "none"
                      : `1px solid ${tokens.slate300}`,
                    color: currentClass?.is_approved
                      ? tokens.white
                      : tokens.slate500,
                    boxShadow: currentClass?.is_approved
                      ? `0 4px 16px ${alpha(tokens.emerald, 0.3)}`
                      : "none",
                    "&:hover": {
                      background: currentClass?.is_approved
                        ? `linear-gradient(135deg, #047857, #065F46)`
                        : tokens.slate100,
                      boxShadow: currentClass?.is_approved
                        ? `0 6px 20px ${alpha(tokens.emerald, 0.4)}`
                        : "none",
                    },
                  }}
                >
                  {currentClass?.is_approved
                    ? "Class Approved"
                    : "Not Approved"}
                </Button>
              </motion.div>
            )}
          </Stack>
        </Paper>
      </motion.div>
      {/* ── Subject Filter ────────────────────────────────────────────────────── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        custom={1}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: tokens.radius,
            background: tokens.white,
            border: `1px solid ${tokens.slate300}`,
            boxShadow: tokens.shadow,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
            <FaFilter size={13} color={tokens.slate500} />
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 800,
                color: tokens.slate500,
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Filter by Subject
            </Typography>
          </Stack>
          <FormControl fullWidth size="small">
            <Select
              value={selectedSubject?.id || ""}
              onChange={(e) => setSelectedSubject(Number(e.target.value))}
              displayEmpty
              sx={{
                color: selectedSubject ? tokens.slate900 : tokens.slate300,
                borderRadius: tokens.radiusSm,
                background: tokens.slate50,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: tokens.slate300,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: tokens.sapphireLight,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: tokens.sapphire,
                },
                "& .MuiSvgIcon-root": { color: tokens.sapphire },
              }}
            >
              <MenuItem value="" disabled sx={{ color: tokens.slate300 }}>
                Select a subject to filter students…
              </MenuItem>
              {studentSubject.map((subj) => (
                <MenuItem
                  key={subj.id}
                  value={subj.id}
                  sx={{ textTransform: "capitalize" }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: tokens.slate700,
                      }}
                    >
                      {subj.subject_name}
                    </Typography>
                    <Chip
                      label={`${subj.students_attended} took exam`}
                      size="small"
                      sx={{
                        ml: 2,
                        fontSize: "0.68rem",
                        height: 22,
                        fontWeight: 700,
                        background: tokens.sapphirePale,
                        color: tokens.sapphire,
                        border: `1px solid ${alpha(tokens.sapphire, 0.2)}`,
                      }}
                    />
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      </motion.div>
      {/* ── Main Card with Tabs ───────────────────────────────────────────────── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        custom={2}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: tokens.radius,
            background: tokens.white,
            border: `1px solid ${tokens.slate300}`,
            boxShadow: tokens.shadowLg,
            overflow: "hidden",
          }}
        >
          {/* Tabs header */}
          <Box
            sx={{
              borderBottom: `1px solid ${tokens.slate100}`,
              px: { xs: 2, md: 3 },
              pt: 1,
              background: tokens.slate50,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  color: tokens.slate500,
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  textTransform: "none",
                  letterSpacing: "0.2px",
                  minHeight: 52,
                  gap: 1,
                  fontFamily: "inherit",
                  "&.Mui-selected": { color: tokens.sapphire },
                },
                "& .MuiTabs-indicator": {
                  background: `linear-gradient(90deg, ${tokens.sapphire}, ${tokens.sapphireLight})`,
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                },
              }}
            >
              <Tab
                icon={<FaGraduationCap size={14} />}
                iconPosition="start"
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <span>Subjects</span>
                    <Box
                      sx={{
                        minWidth: 22,
                        height: 20,
                        px: 0.75,
                        borderRadius: 99,
                        background:
                          activeTab === 0
                            ? tokens.sapphirePale
                            : tokens.slate100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 800,
                          color:
                            activeTab === 0 ? tokens.sapphire : tokens.slate500,
                          lineHeight: 1,
                        }}
                      >
                        {subjects.length}
                      </Typography>
                    </Box>
                  </Stack>
                }
              />
              <Tab
                icon={<FaUsers size={14} />}
                iconPosition="start"
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <span>Students</span>
                    {totalStudents != null && (
                      <Box
                        sx={{
                          minWidth: 22,
                          height: 20,
                          px: 0.75,
                          borderRadius: 99,
                          background:
                            activeTab === 1
                              ? tokens.sapphirePale
                              : tokens.slate100,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.68rem",
                            fontWeight: 800,
                            color:
                              activeTab === 1
                                ? tokens.sapphire
                                : tokens.slate500,
                            lineHeight: 1,
                          }}
                        >
                          {totalStudents}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                }
              />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* ── SUBJECTS TAB ─────────────────────────────────────────────── */}
            <TabPanel value={activeTab} index={0}>
              {/* Add / Edit form */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={editingSubject ? "edit" : "add"}
                  variants={scaleIn}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2, md: 3 },
                      mb: 3,
                      borderRadius: tokens.radiusSm,
                      background: editingSubject
                        ? `linear-gradient(135deg, #FFFBEB, #FEF3C7)`
                        : tokens.sapphirePale,
                      border: `1.5px solid ${
                        editingSubject
                          ? alpha(tokens.amber, 0.3)
                          : alpha(tokens.sapphire, 0.2)
                      }`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: editingSubject ? tokens.amber : tokens.sapphire,
                        mb: 2,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        fontSize: "0.7rem",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      {editingSubject
                        ? "✏️  Editing Subject"
                        : "➕  Add New Subject"}
                    </Typography>

                    <Box
                      component="form"
                      onSubmit={handleSubjectSubmit}
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <TextField
                        sx={{
                          flex: 1,
                          minWidth: 220,
                          "& .MuiOutlinedInput-root": {
                            color: tokens.slate900,
                            borderRadius: tokens.radiusXs,
                            background: tokens.white,
                            fontFamily: "inherit",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            "& fieldset": {
                              borderColor: alpha(tokens.sapphire, 0.25),
                            },
                            "&:hover fieldset": {
                              borderColor: tokens.sapphireLight,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: tokens.sapphire,
                              borderWidth: "1.5px",
                            },
                          },
                          "& input::placeholder": {
                            color: tokens.slate300,
                            fontWeight: 400,
                          },
                        }}
                        value={subjectName}
                        onChange={(e) => setSubjectName(e.target.value)}
                        placeholder="Enter subject / course name…"
                        size="small"
                        required
                      />
                      <motion.div whileTap={{ scale: 0.95 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={loading}
                          startIcon={
                            loading ? (
                              <CircularProgress size={12} color="inherit" />
                            ) : editingSubject ? (
                              <FaCheck size={12} />
                            ) : (
                              <FaPlus size={12} />
                            )
                          }
                          sx={{
                            borderRadius: tokens.radiusXs,
                            px: 3,
                            py: 1,
                            fontWeight: 800,
                            textTransform: "none",
                            fontFamily: "inherit",
                            fontSize: "0.85rem",
                            background: `linear-gradient(135deg, ${tokens.sapphire}, ${tokens.sapphireLight})`,
                            boxShadow: `0 4px 14px ${alpha(
                              tokens.sapphire,
                              0.35
                            )}`,
                            "&:hover": {
                              background: `linear-gradient(135deg, #1E3A8A, ${tokens.sapphire})`,
                              boxShadow: `0 6px 20px ${alpha(
                                tokens.sapphire,
                                0.45
                              )}`,
                            },
                            "&:disabled": { opacity: 0.55 },
                          }}
                        >
                          {loading
                            ? "Saving…"
                            : editingSubject
                            ? "Update Subject"
                            : "Add Subject"}
                        </Button>
                      </motion.div>
                      {editingSubject && (
                        <motion.div whileTap={{ scale: 0.95 }}>
                          <Button
                            variant="outlined"
                            onClick={clearSubjectForm}
                            sx={{
                              borderRadius: tokens.radiusXs,
                              textTransform: "none",
                              fontWeight: 700,
                              fontFamily: "inherit",
                              borderColor: tokens.slate300,
                              color: tokens.slate500,
                              "&:hover": {
                                borderColor: tokens.sapphireLight,
                                background: tokens.sapphirePale,
                                color: tokens.sapphire,
                              },
                            }}
                          >
                            Cancel
                          </Button>
                        </motion.div>
                      )}
                    </Box>
                  </Paper>
                </motion.div>
              </AnimatePresence>

              {/* Subject list */}
              {subjectsLoading ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    style={{ display: "inline-block" }}
                  >
                    <CircularProgress
                      size={32}
                      sx={{ color: tokens.sapphire }}
                    />
                  </motion.div>
                  <Typography
                    sx={{
                      mt: 2,
                      color: tokens.slate500,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  >
                    Loading subjects…
                  </Typography>
                </Box>
              ) : subjects.length === 0 ? (
                <motion.div
                  variants={scaleIn}
                  initial="hidden"
                  animate="visible"
                >
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 8,
                      px: 3,
                      borderRadius: tokens.radiusSm,
                      background: tokens.slate50,
                      border: `1.5px dashed ${tokens.slate300}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        background: tokens.sapphirePale,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <FaGraduationCap size={26} color={tokens.sapphire} />
                    </Box>
                    <Typography
                      sx={{ color: tokens.slate900, fontWeight: 700, mb: 0.5 }}
                    >
                      No subjects yet
                    </Typography>
                    <Typography
                      sx={{ color: tokens.slate500, fontSize: "0.875rem" }}
                    >
                      Add your first subject using the form above.
                    </Typography>
                  </Box>
                </motion.div>
              ) : (
                <Stack spacing={1.5}>
                  <AnimatePresence>
                    {subjects.map((s, i) => (
                      <SubjectCard
                        key={s.id}
                        s={s}
                        index={i}
                        handleSubjectClick={handleSubjectClick}
                        handleEditSubject={handleEditSubject}
                        onConfirmDelete={onConfirmDelete}
                        toggleSubjectActive={toggleSubjectActive}
                        toggleSubjectApprove={toggleSubjectApprove}
                        handleDownloadPDF={handleDownloadPDF}
                        handleDownloadCSV={handleDownloadCSV}
                        isLoading={loadingSubjectId === s.id} // <-- new prop
                        disabled={!!loadingSubjectId} // <-- locks all cards while one loads
                      />
                    ))}
                  </AnimatePresence>
                </Stack>
              )}
            </TabPanel>

            {/* ── STUDENTS TAB ──────────────────────────────────────────────── */}
            <TabPanel value={activeTab} index={1}>
              <InstStudentPage
                classId={classId}
                totalStudents={totalStudents}
                setTotalStudents={setTotalStudents}
                token={token}
                students={students}
                setStudents={setStudents}
                fetchData={fetchData}
                handleDownloadStudentPDF={handleDownloadStudentPDF}
                handleDownloadStudentCSV={handleDownloadStudentCSV}
              />
            </TabPanel>
          </Box>
        </Paper>
      </motion.div>
      <DeleteDialog
        open={openConfirm}
        onClose={closeConfirmDialog}
        onConfirm={handleDeleteSubject}
        title={"subject"}
        loading={loadingDelete}
      />{" "}
      <SubjectModal
        openActivityModal={openActivityModal}
        setOpenActivityModal={setOpenActivityModal}
        handleAssignmentClick={handleAssignmentClick}
        handleExamClick={handleExamClick}
      />
    </Box>
  );
};

export default InstSubjectPage;
